import { Injectable } from '@visiba/instantiation';
import { HttpService, VisibaHttpOption } from '@application/http.service';
import { ApiBase } from './_api_base';
import type { GetRepresentativeNinApiModel } from './generated/general/models';
import { RED_ROBIN_SUPPORTED_LANGUAGES } from '../constants';
import { UniversalConfig } from '@application/universal_config.service';

interface AccessInfo {
	active: boolean;
	isOpen: boolean;
	closedReason: string;
}

@Injectable()
export class RedRobinApiService extends ApiBase {
	constructor(
		private readonly httpService: HttpService,
		private readonly universalConfig: UniversalConfig,
	) {
		super('general');
	}

	public getRepresentativeNin(sessionId: string) {
		return this.httpService.get<GetRepresentativeNinApiModel>(`${this.baseUri}/RedRobin/${sessionId}/GetRepresentativeNin`);
	}

	public getOpenStatus() {
		return this.httpService.get<AccessInfo>(`/client/v2/units/${this.universalConfig.currentUnitId}/RedRobin/accessInfo`);
	}

	public getSessionLink(sessionId: string) {
		const httpOptions = new VisibaHttpOption(this.universalConfig, { parseAs: 'text' });
		return this.httpService.get<string>(
			`/client/v2/units/${this.universalConfig.currentUnitId}/RedRobin/session/${sessionId}/entryUrl`,
			httpOptions,
		);
	}

	public createSession(userIsPatient: boolean, patientNin: Nullable<string>) {
		const getLanguageCode = (): string => {
			const selectedLanguageCode = this.universalConfig.selectedLanguageCode.peek();
			if (RED_ROBIN_SUPPORTED_LANGUAGES.includes(selectedLanguageCode)) {
				return selectedLanguageCode;
			}

			const availableLanguages = this.universalConfig.availableLanguages;
			for (const language of availableLanguages) {
				if (RED_ROBIN_SUPPORTED_LANGUAGES.includes(language)) {
					return language;
				}
			}

			return 'en';
		};

		const httpOptions = new VisibaHttpOption(this.universalConfig, { parseAs: 'text' });
		return this.httpService.post<string>(
			`/client/v2/units/${this.universalConfig.currentUnitId}/redrobin/startSession`,
			{
				userIsPatient,
				patientNin,
				languageCode: getLanguageCode(),
			},
			httpOptions,
		);
	}
}
